import { useEffect, useState } from "react";
import axios from "axios";
import { Eye, EyeOff, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/lib/api";
const EMPTY = { date: "", tag: "Company", title: "", text: "", active: true };
const TAGS = ["Company", "Platform", "Product"];

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-ink outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-navy focus:ring-4 focus:ring-navy/10";

const NewsPanel = () => {
  const [posts, setPosts] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () =>
    axios
      .get(`${API}/news/manage`, { withCredentials: true })
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]));

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await axios.patch(`${API}/news/${editing}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/news`, form, { withCredentials: true });
      }
      toast.success(editing ? "Post updated" : "Post published");
      setForm(EMPTY);
      setEditing(null);
      setShowForm(false);
      load();
    } catch {
      toast.error("Could not save the post");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (n) => {
    setForm({ date: n.date, tag: n.tag, title: n.title, text: n.text, active: n.active });
    setEditing(n.id);
    setShowForm(true);
  };

  const toggleActive = async (n) => {
    try {
      await axios.patch(
        `${API}/news/${n.id}`,
        { date: n.date, tag: n.tag, title: n.title, text: n.text, active: !n.active },
        { withCredentials: true }
      );
      toast.success(n.active ? "Post unpublished" : "Post published");
      load();
    } catch {
      toast.error("Could not update the post");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await axios.delete(`${API}/news/${id}`, { withCredentials: true });
      toast.success("Post deleted");
      load();
    } catch {
      toast.error("Could not delete the post");
    }
  };

  return (
    <div data-testid="news-panel">
      <div className="mt-6 flex justify-end">
        <button
          onClick={() => {
            setForm(EMPTY);
            setEditing(null);
            setShowForm((v) => !v);
          }}
          data-testid="add-news-button"
          className="flex items-center gap-2 rounded-full bg-navy px-6 py-3 font-heading text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800"
        >
          {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showForm ? "Close" : "New Post"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={save}
          data-testid="news-form"
          className="mt-6 rounded-3xl border border-gray-100 bg-white p-8 shadow-sm"
        >
          <h2 className="font-heading text-lg font-bold text-ink">
            {editing ? "Edit post" : "New post"}
          </h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <input
              required
              placeholder="Date label (e.g. August 2026)"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              data-testid="news-date-input"
              className={inputCls}
            />
            <select
              value={form.tag}
              onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))}
              data-testid="news-tag-input"
              className={inputCls}
            >
              {TAGS.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
            <input
              required
              placeholder="Headline"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              data-testid="news-title-input"
              className={`${inputCls} sm:col-span-2`}
            />
            <textarea
              required
              rows={3}
              placeholder="Post text"
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              data-testid="news-text-input"
              className={`${inputCls} resize-none sm:col-span-2`}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="news-save-button"
            className="mt-6 flex items-center gap-2 rounded-full bg-navy px-8 py-3 font-heading text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Publish Post"}
          </button>
        </form>
      )}

      {posts === null ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-navy" />
        </div>
      ) : posts.length === 0 ? (
        <div className="mt-10 rounded-3xl border border-gray-100 bg-white py-16 text-center text-sm text-fog">
          No posts yet. Publish your first update.
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {posts.map((n) => (
            <article
              key={n.id}
              data-testid="admin-news-card"
              className={`rounded-3xl border border-gray-100 bg-white p-7 shadow-sm ${n.active ? "" : "opacity-60"}`}
            >
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-heading text-lg font-bold text-ink">{n.title}</h2>
                <span className="rounded-full bg-gold/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-gold-dark">
                  {n.tag}
                </span>
                <span
                  className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${
                    n.active ? "bg-emerald-50 text-emerald-600" : "bg-mist text-fog"
                  }`}
                >
                  {n.active ? "Live" : "Hidden"}
                </span>
              </div>
              <p className="mt-1 text-xs text-fog">{n.date}</p>
              <p className="mt-2 max-w-2xl text-sm text-fog">{n.text}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  onClick={() => startEdit(n)}
                  data-testid={`news-edit-${n.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-royal hover:text-royal"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => toggleActive(n)}
                  data-testid={`news-toggle-${n.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-gold hover:text-gold-dark"
                >
                  {n.active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  {n.active ? "Unpublish" : "Publish"}
                </button>
                <button
                  onClick={() => remove(n.id)}
                  data-testid={`news-delete-${n.id}`}
                  className="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-red-400 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsPanel;
